/**
 * @render react
 * @name Rating
*/

import React from 'react';
import PropTypes from 'prop-types';
import Ratings from 'react-ratings-declarative';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Text } from 'Components/atoms/Text';

/**
 * Rating component
 * @param {string} label - label for component
 * @param {number} rating - rating to show the filled icons
 * @param {string} iconPath - d path of the SVG icon
 * @param {string} iconViewBox - viewBox of the SVG icon
 * @param {string} fill - color for the filled icons (can be name, hex, rgb, rgba)
 * @param {string} fillInactive - color for the unfilled icons (can be name, hex, rgb, rgba)
 * @param {number} numberOfStars - number of stars to display
 * @param {string} widgetSpacings - amount of spacing between icons
 * @param {string} widgetDimensions - size of icon
 * @param {boolean} showTextRating - whether to display the text rating
 * @param {boolean} showStarRating - whether to display the star rating
 * @param {boolean} extraContent - extra content to display
 * @param {Object} context - extra information coming from Polaris provider
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {number} rounding - rounding for ratings
 */

const Rating = ({
  context: {
    cssPrefix,
    ratingConfig,
    ratingFillColor,
    ratingFillInactiveColor,
  },
  extraClassNames,
  extraContent,
  fill,
  fillInactive,
  iconPath,
  iconViewBox,
  label,
  loadStyles,
  numberOfStars,
  rating,
  rounding,
  showStarRating,
  showTextRating,
  showTextRatingWithTotal,
  showZero,
  widgetDimensions,
  widgetSpacings,
}) => (
  <div
    className={classNames({
      [`${cssPrefix}__rating`]: loadStyles,
      ...extraClassNames,
    })}
  >
    {label && (
      <div className={`${cssPrefix}__rating--label`}>
        {label}
      </div>
    )}

    {showStarRating && (
      <div
        className={classNames({
          [`${cssPrefix}__rating--stars`]: loadStyles,
        })}
      >
        <Ratings
          rating={rating || 0}
          {...ratingConfig}
          svgIconViewBoxes={iconViewBox || (ratingConfig && ratingConfig.svgIconViewBoxes ? ratingConfig.svgIconViewBoxes : undefined)}
          svgIconPaths={iconPath || (ratingConfig && ratingConfig.svgIconPaths ? ratingConfig.svgIconPaths : undefined)}
          widgetRatedColors={fill || ratingFillColor}
          widgetEmptyColors={fillInactive || ratingFillInactiveColor}
          widgetDimensions={widgetDimensions}
          widgetSpacings={widgetSpacings}
        >
          {[...Array(numberOfStars)].map((r, index) => <Ratings.Widget key={`rating-${index}`} />)}
        </Ratings>
      </div>
    )}

    {showTextRating && rating && (rating > 0 || (showZero && rating >= 0)) && (
      <p
        className={classNames({
          [`${cssPrefix}__rating--text`]: loadStyles,
        })}
      >
        {showTextRatingWithTotal ? (
          <>
            <span>
              {rating.toFixed(rounding)}
            </span>
            {' '}
            out of
            {' '}
            <span>
              {numberOfStars}
            </span>
          </>
        ) : (
          rating.toFixed(rounding)
        )}
      </p>
    )}

    {extraContent && (
      <div
        className={classNames({
          [`${cssPrefix}__rating--extra`]: loadStyles,
        })}
      >
        <Text>{extraContent}</Text>
      </div>
    )}
  </div>
);

Rating.propTypes = {
  extraContent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.bool,
  ]),
  context: PropTypes.shape(),
  extraClassNames: PropTypes.instanceOf(Object),
  fill: PropTypes.string,
  fillInactive: PropTypes.string,
  iconPath: PropTypes.string,
  iconViewBox: PropTypes.string,
  label: PropTypes.string,
  loadStyles: PropTypes.bool,
  numberOfStars: PropTypes.number,
  rating: PropTypes.number,
  rounding: PropTypes.number,
  showStarRating: PropTypes.bool,
  showTextRating: PropTypes.bool,
  showTextRatingWithTotal: PropTypes.bool,
  showZero: PropTypes.bool,
  widgetDimensions: PropTypes.string,
  widgetSpacings: PropTypes.string,
};

Rating.defaultProps = {
  context: {},
  extraClassNames: {},
  extraContent: false,
  fill: null,
  fillInactive: null,
  iconPath: null,
  iconViewBox: null,
  label: '',
  loadStyles: true,
  numberOfStars: 5,
  rating: 0,
  rounding: 0,
  showStarRating: true,
  showTextRating: false,
  showTextRatingWithTotal: true,
  showZero: false,
  widgetDimensions: '22px',
  widgetSpacings: '2.5px',
};

export default withPolaris(Rating);
